import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useMutation } from 'graphql-hooks';
import { useTranslation } from 'react-i18next';
import { useSetAtom } from 'jotai';

import { LIKE_VIDEO, UNLIKE_VIDEO } from '../graphql/like/mutation';
import { FAVORITE_VIDEO, UNFAVORITE_VIDEO } from '../graphql/favorite/mutation';
import { FOLLOW_USER } from '../graphql/follow/mutation';
import { SHARE_VIDEO } from '../graphql/share/mutation';
import { VIEW_VIDEO } from '../graphql/view/mutation';
import { toastAtom } from '../atoms';
import { getShortUrl } from '../helpers';
import { Video } from '../types';

// A video watched 2 seconds counts as a view.
const VIEW_DELAY = 2000;
const SHARE_LABEL_DELAY = 2000;

interface VideoActionsOptions {
  // Video on screen, the actions apply to it.
  current?: Video;
  setVideos: React.Dispatch<React.SetStateAction<Video[]>>;
  // A visitor has no views and his shares are not counted.
  isGuest: boolean;
}

// Actions of the column next to the player (feed, ranking): optimistic, undone on an error.
const useVideoActions = (options: VideoActionsOptions) => {
  const { current, setVideos, isGuest } = options;
  const { t } = useTranslation();
  const [likeVideo] = useMutation(LIKE_VIDEO);
  const [unLikeVideo] = useMutation(UNLIKE_VIDEO);
  const [favoriteVideo] = useMutation(FAVORITE_VIDEO);
  const [unFavoriteVideo] = useMutation(UNFAVORITE_VIDEO);
  const [followUser] = useMutation(FOLLOW_USER);
  const [shareVideo] = useMutation(SHARE_VIDEO);
  const [viewVideo] = useMutation(VIEW_VIDEO);
  const [shareLabel, setShareLabel] = useState('');
  const setToast = useSetAtom(toastAtom);
  const shareTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // One view per video, after 2 seconds on screen.
  const currentId = current ? current.id : '';
  const currentViewed = !current || !!current.isViewed;
  useEffect(() => {
    if (!currentId || currentViewed || isGuest) {
      return undefined;
    }
    const id = currentId;
    const timer = setTimeout(() => {
      viewVideo({ variables: { id } });
      setVideos((prev) => prev.map((v) => (v.id === id ? { ...v, isViewed: true, views: (v.views || 0) + 1 } : v)));
    }, VIEW_DELAY);
    return () => clearTimeout(timer);
  }, [currentId, currentViewed, isGuest, setVideos, viewVideo]);

  useEffect(() => () => {
    if (shareTimerRef.current) {
      clearTimeout(shareTimerRef.current);
    }
  }, []);

  const updateVideo = useCallback((id: string, change: (v: Video) => Video) => {
    setVideos((prev) => prev.map((v) => (v.id === id ? change(v) : v)));
  }, [setVideos]);

  const onLike = useCallback(async () => {
    if (!current) {
      return;
    }
    const { id, isLiked } = current;
    const toggle = (v: Video) => ({ ...v, isLiked: !v.isLiked, likes: Math.max(0, (v.likes || 0) + (v.isLiked ? -1 : 1)) });
    updateVideo(id, toggle);
    const { error } = await (isLiked ? unLikeVideo : likeVideo)({ variables: { id } });
    if (error) {
      updateVideo(id, toggle);
    }
  }, [current, likeVideo, unLikeVideo, updateVideo]);

  // Private list of the user (Favoris tab of his profile): the creator only gets a notification.
  const onFavorite = useCallback(async () => {
    if (!current) {
      return;
    }
    const { id, isFavorite } = current;
    const toggle = (v: Video) => ({ ...v, isFavorite: !v.isFavorite, favorites: Math.max(0, (v.favorites || 0) + (v.isFavorite ? -1 : 1)) });
    updateVideo(id, toggle);
    const { error } = await (isFavorite ? unFavoriteVideo : favoriteVideo)({ variables: { id } });
    if (error) {
      updateVideo(id, toggle);
      return;
    }
    // Like the app: the private list of the profile is told.
    setToast({ id: Date.now(), text: t(isFavorite ? 'admin.feed.favoriteRemoved' : 'admin.feed.favoriteAdded') });
  }, [current, favoriteVideo, setToast, t, unFavoriteVideo, updateVideo]);

  // Every video of the followed player loses its "+".
  const onFollow = useCallback(async () => {
    if (!current) {
      return;
    }
    const { userId } = current;
    setVideos((prev) => prev.map((v) => (v.userId === userId ? { ...v, isFollowed: true } : v)));
    const { error } = await followUser({ variables: { id: userId } });
    if (error) {
      setVideos((prev) => prev.map((v) => (v.userId === userId ? { ...v, isFollowed: false } : v)));
    }
  }, [current, followUser, setVideos]);

  const onShare = useCallback(async () => {
    if (!current) {
      return;
    }
    const { id } = current;
    try {
      await navigator.clipboard.writeText(getShortUrl(current));
    } catch (e) {
      return;
    }
    setShareLabel(t('admin.feed.linkCopied'));
    if (shareTimerRef.current) {
      clearTimeout(shareTimerRef.current);
    }
    shareTimerRef.current = setTimeout(() => setShareLabel(''), SHARE_LABEL_DELAY);
    // The link works for everybody, the share is only counted for a connected user.
    if (isGuest) {
      return;
    }
    const { data } = await shareVideo({ variables: { input: { id, to: 'copylink' } } }) as any;
    if (data && data.shareVideo && data.shareVideo.video) {
      const { shares } = data.shareVideo.video;
      updateVideo(id, (v) => ({ ...v, shares }));
    }
  }, [current, isGuest, shareVideo, t, updateVideo]);

  const onCommented = useCallback(() => {
    if (current) {
      updateVideo(current.id, (v) => ({ ...v, comments: (v.comments || 0) + 1 }));
    }
  }, [current, updateVideo]);

  return {
    onCommented,
    onFavorite,
    onFollow,
    onLike,
    onShare,
    shareLabel,
  };
};

export default useVideoActions;
